import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { TrendingUp, Sparkles } from "lucide-react";
import PageSEO from '../components/PageSEO';

export default function AuthPage({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('tab') !== 'register');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Client-side validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address (missing @ or .)");
      setLoading(false);
      return;
    }

    if (!isLogin && formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      setLoading(false);
      return;
    }

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(endpoint, payload);

      toast.success(
        isLogin ? "Welcome back!" : "Account created successfully!"
      );
      onLogin(response.data.token, response.data.tenant);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background transition-colors flex flex-col">
      <PageSEO title="Login | AI Search Advisers" isPrivate={true} />
      <div className="flex-grow flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#FEAB3D] to-[#e8960a] rounded-xl flex items-center justify-center shadow-md">
              <Sparkles className="w-6 h-6 text-[#021426]" />
            </div>
            <span className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">
              AI Search <span className="text-[#FEAB3D]">Advisor</span>
            </span>
          </div>
          <p className="text-slate-600 dark:text-slate-400">AI-powered analytics platform</p>
        </div>

        {/* Auth Card */}
        <div
          className="bg-white dark:bg-card rounded-2xl shadow-xl dark:shadow-none border border-transparent dark:border-border p-8"
          data-testid="auth-form"
        >
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {isLogin ? "Welcome back" : "Create account"}
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              {isLogin
                ? "Sign in to your account"
                : "Get started with AI Search Advisers"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-slate-700 dark:text-slate-300">
                  Full Name
                </Label>
                <Input
                  id="name"
                  data-testid="input-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  className="mt-1"
                  placeholder="John Doe"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-slate-700 dark:text-slate-300">
                Email
              </Label>
              <Input
                id="email"
                data-testid="input-email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                className="mt-1"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 dark:text-slate-300">
                Password
              </Label>
              <Input
                id="password"
                data-testid="input-password"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required
                className="mt-1"
                placeholder="••••••••"
              />
            </div>

            <Button
              data-testid="submit-auth-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#FEAB3D] text-[#021426] hover:bg-[#FEAB3D] dark:hover:bg-[#e8960a] py-3 rounded-none font-medium custom-dashboard-btn"
            >
              {loading
                ? "Processing..."
                : isLogin
                  ? "Sign In"
                  : "Create Account"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode-button"
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#021426] dark:text-[#FEAB3D] hover:text-[#021426] dark:hover:text-[#e8960a] font-medium"
            >
              {isLogin
                ? "Don't have an account? Sign up"
                : "Already have an account? Sign in"}
            </button>
          </div>
          <div className="mt-3 text-center">
            <button
              data-testid="home-redirect-button"
              type="button"
              onClick={() => (window.location.href = "/")}
              className="text-[#021426] dark:text-[#FEAB3D] hover:text-[#0A4378] font-medium"
            >
              Go to Home
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
